import * as R from 'ramda'

import {
  SoccerAttributeId,
  SoccerEventStatusId,
  SoccerIncidentId,
} from '@collector/sportsapi-client-legacy'

import {
  BlockedIncidentsMap,
  getPitchIncidentIds,
  PitchIncidents,
  PitchIncidentsConfig,
} from '../../configuration'

const pitchIncidents: PitchIncidents<SoccerIncidentId, SoccerAttributeId> = {
  gridClass: 'grid-cols-6',
  incidentButtonData: [
    {
      incidentId: SoccerIncidentId.Goal,
      class: 'col-span-4 col-start-2 @2md:col-start-1 @2md:col-span-6 row-1',
      withIcon: 'left',
      canAddOneTbd: true,
    },
    {
      incidentId: SoccerIncidentId.Penalty,
      class: 'col-span-2 row-2 @2md:col-span-3',
      withIcon: 'top',
      canAddOneTbd: true,
    },
    {
      incidentId: SoccerIncidentId.PenaltySavedByGoalkeeper,
      class: 'col-span-2 row-2 @2md:col-span-3 @2md:row-7',
      name: 'Pen. saved',
    },
    {
      incidentId: SoccerIncidentId.MissedPenalty,
      class: 'col-span-2 row-2 @2md:col-span-3 @2md:row-7',
      name: 'Pen. missed',
    },
    {
      incidentId: SoccerIncidentId.YellowCard,
      class: 'col-span-2 row-3 @2md:col-span-3',
      name: 'YC',
      withIcon: 'top',
    },
    {
      incidentId: SoccerIncidentId.RedCard,
      class: 'col-span-2 row-3 @2md:col-span-3',
      name: 'RC',
      withIcon: 'top',
    },
    {
      incidentId: SoccerIncidentId.Corner,
      class: 'col-span-2 row-3 @2md:row-2 @2md:col-span-3',
      withIcon: 'top',
    },
    {
      incidentId: SoccerIncidentId.ShotBlocked,
      class: 'col-span-2 row-4 @2md:col-span-3 @2md:row-5',
    },
    {
      incidentId: SoccerIncidentId.ShotOffTarget,
      class: 'col-span-2 row-4 @2md:col-span-3 @2md:col-start-4',
      name: 'Shot OFF',
    },
    {
      incidentId: SoccerIncidentId.ShotOnTarget,
      class: 'col-span-2 row-4 @2md:col-span-3 @2md:col-start-1',
      name: 'Shot ON',
    },
    {
      incidentId: SoccerIncidentId.Offside,
      class: 'col-span-2 row-5 @2md:col-span-3 @2md:row-6',
    },
    {
      incidentId: SoccerIncidentId.Foul,
      class: 'col-span-2 row-5 @2md:col-span-3 @2md:row-6',
    },
    {
      incidentId: SoccerIncidentId.ThrowIn,
      class: 'col-span-2 row-5 @2md:col-span-3',
    },
    {
      incidentId: SoccerIncidentId.SubstitutionIn,
      class: 'col-span-2 row-6 @2md:row-8 @2md:col-start-3',
      name: 'Subs',
    },
    {
      incidentId: SoccerIncidentId.Injury,
      class: 'col-span-2 row-6 @2md:row-8 @2md:col-start-1',
    },
    {
      incidentId: SoccerIncidentId.GoalKick,
      class: 'col-span-2 row-6 @2md:row-8 @2md:col-start-5',
    },
  ],
}

const eventStatusToBlockedPitchIncidentsTextMap = new Map<
  SoccerEventStatusId,
  string
>([
  [SoccerEventStatusId.NotStarted, 'start the match'],
  [SoccerEventStatusId.StartDelayed, 'start the match'],
  [SoccerEventStatusId.WaitingForPenalty, 'start the penalty shootout'],
])

const pitchIncidentIds = getPitchIncidentIds(pitchIncidents.incidentButtonData)

const pitchIncidentIdsWithoutSubstitutionIn: Set<SoccerIncidentId> = new Set(
  R.without([SoccerIncidentId.SubstitutionIn], [...pitchIncidentIds]),
)

const blockedPitchIncidentsMap: BlockedIncidentsMap<
  SoccerEventStatusId,
  SoccerIncidentId
> = new Map([
  [SoccerEventStatusId.Abandoned, pitchIncidentIds],
  [SoccerEventStatusId.Cancelled, pitchIncidentIds],
  [SoccerEventStatusId.NotStarted, pitchIncidentIds],
  [SoccerEventStatusId.StartDelayed, pitchIncidentIds],
  [SoccerEventStatusId.Postponed, pitchIncidentIds],
  [SoccerEventStatusId.Halftime, pitchIncidentIdsWithoutSubstitutionIn],
  [
    SoccerEventStatusId.WaitingForExtratime,
    pitchIncidentIdsWithoutSubstitutionIn,
  ],
  [
    SoccerEventStatusId.ExtratimeHalftime,
    pitchIncidentIdsWithoutSubstitutionIn,
  ],
  [SoccerEventStatusId.WaitingForPenalty, pitchIncidentIds],
  [
    SoccerEventStatusId.PenaltyShootout,
    new Set(
      R.without(
        [
          SoccerIncidentId.Goal,
          SoccerIncidentId.YellowCard,
          SoccerIncidentId.RedCard,
          SoccerIncidentId.MissedPenalty,
        ],
        [...pitchIncidentIds],
      ),
    ),
  ],
  [SoccerEventStatusId.FinishedAfterExtratime, pitchIncidentIds],
  [SoccerEventStatusId.FinishedAfterPenalties, pitchIncidentIds],
  [SoccerEventStatusId.FinishedAwardedWin, pitchIncidentIds],
  [SoccerEventStatusId.FinishedRegularTime, pitchIncidentIds],
  [SoccerEventStatusId.Interrupted, pitchIncidentIds],
  [SoccerEventStatusId.ToFinish, pitchIncidentIds],
])

export const pitchIncidentsConfig: PitchIncidentsConfig<
  SoccerEventStatusId,
  SoccerIncidentId,
  SoccerAttributeId
> = {
  pitchIncidents,
  blockedPitchIncidentsMap,
  eventStatusToBlockedPitchIncidentsTextMap,
}
